import type { ScreeningBucket } from "@/components/guardians/screenings/useScreenings";
import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import type { AppLocationCodec } from "@/navigation/appLocation";
import {
  type GuardiansView,
  isScreeningDocumentId,
  normalizeGuardiansScreeningParam,
  patchSearchParams,
  parseScreeningBucket,
  resolveGuardiansView,
} from "@/components/guardians/guardiansParams";

export { isScreeningDocumentId } from "@/components/guardians/guardiansParams";
export type { GuardiansView };

export type GuardiansLocation = {
  view?: GuardiansView;
  screeningId?: string;
  auditId?: string;
  alertId?: string;
  screeningOutcome?: ScreeningBucket;
  missionId?: string;
  missionTemplateId?: string;
  taskId?: string;
  agentId?: string;
  catalogueId?: string;
  catalogueKey?: string;
  ruleId?: string;
  auditOrigin?: string;
  auditType?: string;
  auditsMode?: string;
  rating?: string;
  settings?: string;
  conversation?: string;
  timePeriod?: string;
  category?: string;
  audience?: string;
  interaction?: string;
};

const LOCATION_FIELD_KEYS: (keyof GuardiansLocation)[] = [
  "view",
  "screeningId",
  "auditId",
  "alertId",
  "screeningOutcome",
  "missionId",
  "missionTemplateId",
  "taskId",
  "agentId",
  "catalogueId",
  "catalogueKey",
  "ruleId",
  "auditOrigin",
  "auditType",
  "auditsMode",
  "rating",
  "settings",
  "conversation",
  "timePeriod",
  "category",
  "audience",
  "interaction",
];

export function guardiansDefaults(): GuardiansLocation {
  return { view: "alerts" };
}

function readOptionalString(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function screeningOutcomeFromParams(
  screeningOutcomeRaw: string | null | undefined,
  screeningRaw: string | null | undefined,
): ScreeningBucket | undefined {
  const explicit = parseScreeningBucket(screeningOutcomeRaw ?? null);
  if (explicit !== "all") return explicit;
  const fromLegacy = normalizeGuardiansScreeningParam(screeningRaw ?? null).screeningOutcomePatch;
  if (fromLegacy === "passed" || fromLegacy === "flagged" || fromLegacy === "failed") {
    return fromLegacy;
  }
  return undefined;
}

function normalizeGuardiansLocation(loc: GuardiansLocation): GuardiansLocation {
  const view = loc.view ?? "alerts";
  const next: GuardiansLocation = { view };
  if (loc.screeningId) next.screeningId = loc.screeningId;
  if (loc.auditId) next.auditId = loc.auditId;
  if (loc.alertId) next.alertId = loc.alertId;
  if (loc.screeningOutcome && loc.screeningOutcome !== "all") {
    next.screeningOutcome = loc.screeningOutcome;
  }
  if (loc.missionId) next.missionId = loc.missionId;
  if (loc.missionTemplateId) next.missionTemplateId = loc.missionTemplateId;
  if (loc.taskId) next.taskId = loc.taskId;
  if (loc.agentId) next.agentId = loc.agentId;
  if (loc.catalogueId) next.catalogueId = loc.catalogueId;
  if (loc.catalogueKey) next.catalogueKey = loc.catalogueKey;
  if (loc.ruleId) next.ruleId = loc.ruleId;
  if (loc.auditOrigin && loc.auditOrigin !== "all") next.auditOrigin = loc.auditOrigin;
  if (loc.auditType && loc.auditType !== "all") next.auditType = loc.auditType;
  if (loc.auditsMode) next.auditsMode = loc.auditsMode;
  if (loc.rating && loc.rating !== "all") next.rating = loc.rating;
  if (loc.settings) next.settings = loc.settings;
  if (loc.conversation) next.conversation = loc.conversation;
  if (loc.timePeriod && loc.timePeriod !== "1m") next.timePeriod = loc.timePeriod;
  if (loc.category && loc.category !== "all") next.category = loc.category;
  if (loc.audience && loc.audience !== "all") next.audience = loc.audience;
  if (loc.interaction) next.interaction = loc.interaction;
  return next;
}

function fromParams(params: Record<string, string | undefined>): GuardiansLocation {
  const get = (key: string) => params[key] ?? null;

  const auditsMode = readOptionalString(get("audits_mode"));
  const view = resolveGuardiansView(get("view"), auditsMode ?? null);
  const rawScreening = get("screening");
  const { screeningId, screeningOutcomePatch } = normalizeGuardiansScreeningParam(rawScreening);
  const screeningOutcome = screeningOutcomeFromParams(get("screening_outcome"), rawScreening);

  const loc: GuardiansLocation = { view };
  if (screeningId) loc.screeningId = screeningId;
  if (screeningOutcome) loc.screeningOutcome = screeningOutcome;
  else if (
    screeningOutcomePatch === "passed" ||
    screeningOutcomePatch === "flagged" ||
    screeningOutcomePatch === "failed"
  ) {
    loc.screeningOutcome = screeningOutcomePatch;
  }

  const auditId = readOptionalString(get("audit"));
  if (auditId) loc.auditId = auditId;
  const alertId = readOptionalString(get("alert"));
  if (alertId) loc.alertId = alertId;

  const missionId = readOptionalString(get("mission"));
  if (missionId) loc.missionId = missionId;
  const missionTemplateId = readOptionalString(get("mission_template"));
  if (missionTemplateId) loc.missionTemplateId = missionTemplateId;
  const taskId = readOptionalString(get("task"));
  if (taskId) loc.taskId = taskId;
  const agentId = readOptionalString(get("agent"));
  if (agentId) loc.agentId = agentId;
  const catalogueId = readOptionalString(get("catalogue"));
  if (catalogueId) loc.catalogueId = catalogueId;
  const catalogueKey = readOptionalString(get("catalogue_key"));
  if (catalogueKey) loc.catalogueKey = catalogueKey;
  const ruleId = readOptionalString(get("rule_id"));
  if (ruleId) loc.ruleId = ruleId;

  const auditOrigin = readOptionalString(get("audit_origin"));
  if (auditOrigin) loc.auditOrigin = auditOrigin;
  const auditType = readOptionalString(get("audit_type"));
  if (auditType) loc.auditType = auditType;
  if (auditsMode) loc.auditsMode = auditsMode;

  const rating = readOptionalString(get("rating"));
  if (rating) loc.rating = rating;
  const settings = readOptionalString(get("settings"));
  if (settings) loc.settings = settings;
  const conversation = readOptionalString(get("conversation"));
  if (conversation) loc.conversation = conversation;
  const timePeriod = readOptionalString(get("time_period"));
  if (timePeriod) loc.timePeriod = timePeriod;
  const category = readOptionalString(get("category"));
  if (category) loc.category = category;
  const audience = readOptionalString(get("audience"));
  if (audience) loc.audience = audience;
  const interaction = readOptionalString(get("interaction"));
  if (interaction) loc.interaction = interaction;

  return normalizeGuardiansLocation(loc);
}

function guardiansPathSegments(pathname: string): string[] {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "ops" || segments[1] !== "guardians") return [];
  return segments.slice(2);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): GuardiansLocation {
  const pathRest = guardiansPathSegments(pathname);
  const thirdSegment = pathRest[0];
  const screeningSegment = pathRest[1];

  if (thirdSegment === "screenings") {
    const loc = fromParams(Object.fromEntries(searchParams));
    loc.view = "screenings";
    if (screeningSegment && isScreeningDocumentId(screeningSegment)) {
      loc.screeningId = screeningSegment;
    }
    return normalizeGuardiansLocation(loc);
  }

  return fromParams(Object.fromEntries(searchParams));
}

function applyLocationToSearchParams(
  loc: GuardiansLocation,
  options: { omitPathScreeningFields?: boolean },
): URLSearchParams {
  const normalized = normalizeGuardiansLocation(loc);
  const params = new URLSearchParams();

  const omitScreeningPathFields =
    options.omitPathScreeningFields && normalized.view === "screenings";

  if (normalized.view && normalized.view !== "alerts" && !omitScreeningPathFields) {
    params.set("view", normalized.view);
  }

  if (normalized.screeningId && !omitScreeningPathFields) {
    params.set("screening", normalized.screeningId);
  } else if (normalized.screeningOutcome) {
    params.set("screening", normalized.screeningOutcome);
  }
  if (normalized.screeningOutcome) {
    params.set("screening_outcome", normalized.screeningOutcome);
  }
  if (normalized.auditId) params.set("audit", normalized.auditId);
  if (normalized.alertId) params.set("alert", normalized.alertId);
  if (normalized.missionId) params.set("mission", normalized.missionId);
  if (normalized.missionTemplateId) params.set("mission_template", normalized.missionTemplateId);
  if (normalized.taskId) params.set("task", normalized.taskId);
  if (normalized.agentId) params.set("agent", normalized.agentId);
  if (normalized.catalogueId) params.set("catalogue", normalized.catalogueId);
  if (normalized.catalogueKey) params.set("catalogue_key", normalized.catalogueKey);
  if (normalized.ruleId) params.set("rule_id", normalized.ruleId);
  if (normalized.auditOrigin) params.set("audit_origin", normalized.auditOrigin);
  if (normalized.auditType) params.set("audit_type", normalized.auditType);
  if (normalized.auditsMode) params.set("audits_mode", normalized.auditsMode);
  if (normalized.rating) params.set("rating", normalized.rating);
  if (normalized.settings) params.set("settings", normalized.settings);
  if (normalized.conversation) params.set("conversation", normalized.conversation);
  if (normalized.timePeriod) params.set("time_period", normalized.timePeriod);
  if (normalized.category) params.set("category", normalized.category);
  if (normalized.audience) params.set("audience", normalized.audience);
  if (normalized.interaction) params.set("interaction", normalized.interaction);

  return params;
}

function serializeGuardiansToPathAndSearch(loc: GuardiansLocation): {
  pathname: string;
  searchParams: URLSearchParams;
} {
  const normalized = normalizeGuardiansLocation(loc);

  if (
    normalized.view === "screenings" &&
    normalized.screeningId &&
    isScreeningDocumentId(normalized.screeningId)
  ) {
    return {
      pathname: `/ops/guardians/screenings/${encodeURIComponent(normalized.screeningId)}`,
      searchParams: applyLocationToSearchParams(normalized, { omitPathScreeningFields: true }),
    };
  }

  if (normalized.view === "screenings") {
    return {
      pathname: "/ops/guardians/screenings",
      searchParams: applyLocationToSearchParams(normalized, { omitPathScreeningFields: true }),
    };
  }

  return {
    pathname: "/ops/guardians",
    searchParams: applyLocationToSearchParams(normalized, { omitPathScreeningFields: false }),
  };
}

function toParams(loc: GuardiansLocation): Record<string, string> {
  const params = applyLocationToSearchParams(normalizeGuardiansLocation(loc), {
    omitPathScreeningFields: false,
  });
  const out: Record<string, string> = {};
  params.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

function toUrl(loc: GuardiansLocation): string {
  const { pathname, searchParams } = serializeGuardiansToPathAndSearch(loc);
  const qs = searchParams.toString();
  return `${pathname}${qs ? `?${qs}` : ""}`;
}

export const guardiansCodec: AppLocationCodec<GuardiansLocation> = {
  appId: "ops-guardians",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

function mergeParamPatch(
  current: Record<string, string>,
  patch: Record<string, string | null>,
): Record<string, string> {
  const next = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (value == null || value === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}

/** Apply URL/window param keys to location; clears fields removed from params. */
export function guardiansParamPatch(
  loc: GuardiansLocation,
  patch: Record<string, string | null>,
): Partial<GuardiansLocation> {
  const merged = mergeParamPatch(guardiansCodec.toParams(loc), patch);
  const next = guardiansCodec.fromParams(merged);
  const out: Partial<GuardiansLocation> = { ...next };
  for (const key of LOCATION_FIELD_KEYS) {
    if (next[key] === undefined && loc[key] !== undefined) {
      out[key] = undefined;
    }
  }
  return out;
}

/** Absolute, shareable deep link into Guardians (path + query from location module). */
export function guardiansShareUrl(params: Record<string, string | null>): string {
  const next = patchSearchParams(new URLSearchParams(), params);
  return toAbsoluteUrl(guardiansCodec.toUrl(guardiansCodec.fromParams(Object.fromEntries(next))));
}

export interface GuardiansAlertsUrlInput {
  missionId?: string | null;
  taskId?: string | null;
  alertId?: string | null;
}

export function guardiansAlertsUrl(input: GuardiansAlertsUrlInput): string {
  const { missionId, taskId, alertId } = input;
  return guardiansShareUrl({
    view: "alerts",
    mission: missionId ?? null,
    task: taskId ?? null,
    alert: alertId ?? null,
  });
}

export function guardiansRulesUrl(input: {
  catalogueKey: string;
  ruleId?: string | null;
}): string {
  return guardiansShareUrl({
    view: "rules",
    catalogue_key: input.catalogueKey,
    rule_id: input.ruleId ?? null,
  });
}

export function guardiansConversationUrl(input: {
  missionId?: string | null;
  taskId?: string | null;
  focus?: string | null;
}): string {
  const params = new URLSearchParams();
  if (input.missionId) params.set("mission", input.missionId);
  if (input.taskId) params.set("task", input.taskId);
  if (input.focus) params.set("focus", input.focus);
  const qs = params.toString();
  return toAbsoluteUrl(`/ops/guardians/conversation${qs ? `?${qs}` : ""}`);
}

/** @deprecated Use guardiansAlertsUrl — timeline tab removed. */
export function guardiansTimelineUrl(input: GuardiansAlertsUrlInput): string {
  return guardiansAlertsUrl(input);
}
